<script setup>
import { onMounted, onUnmounted, onBeforeUnmount, ref, watch, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import GenderFilter from './GenderFilter.vue'
import { useStateStore } from '../stores/state.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'
import { formatMarkerText } from '../utils/marker-formatter.js'
import EventSwitcher from './EventSwitcher.vue'

const resultsStore = useResultsStore()
const stateStore = useStateStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)
stateStore.fetchEvents()

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
      resultsStore.fetchResults(route.params.eventAlias)
    }, 30000)
let timer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows by result.id and the timestamp of change
const recentlyChanged = ref({})
const showElapsedTime = ref(true)

// Helper to compare results and mark changed rows
let lastResults = []

watch(
  () => resultsStore.results.map(r => ({ id: r.id, score: r.score, finish_time: r.finish_time, finish_time_gps: r.finish_time_gps, dnf: r.dnf, abs: r.abs, categoryPosition: r.categoryPosition, penaltySeconds: r.penaltySeconds, penaltyComment: r.penaltyComment })),
  (newResults) => {
    const nowTime = Date.now()
    if (lastResults.length === 0) {
      lastResults = JSON.parse(JSON.stringify(newResults))
      return
    }
    const prevById = new Map(lastResults.map(lr => [lr.id, lr]))
    newResults.forEach((r) => {
      const prev = prevById.get(r.id)
      if (
        !prev ||
        prev.score !== r.score ||
        prev.finish_time !== r.finish_time ||
        prev.finish_time_gps !== r.finish_time_gps ||
        prev.dnf !== r.dnf ||
        prev.abs !== r.abs ||
        prev.categoryPosition !== r.categoryPosition
      ) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = JSON.parse(JSON.stringify(newResults))
  },
  { immediate: true }
)

// Computed property for filtered results based on search
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }

  const query = searchQuery.value.trim().toLowerCase()
  return resultsStore.results.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return (nameMatch || numberMatch) && result.category_id !== 10
  }).slice(0, 10)
})

watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
}

const CLEANUP_INTERVAL = 1000
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  timer = setInterval(() => {
    now.value = Date.now()
    const cutoff = Date.now() - 30 * 1000
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, CLEANUP_INTERVAL)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

function calculateTimeFromStart(result) {
  const startTime = new Date(result.start_time)
  const elapsed = new Date(now.value - startTime)
  return getHHMMSSString(elapsed)
}

function timeFromStart(result) {
  const startTime = new Date(result.start_time)
  const nowDate = new Date()
  const elapsed = new Date(nowDate - startTime)
  return elapsed
}

function timeFromStartToSplit(result, split) {
    const startTime = new Date(result.start_time)
    const splitTime = new Date(split.time)
    const elapsed = new Date(splitTime - startTime)
    return elapsed
}

function getHHMMSSString(date) {
  const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
  if (timestamp <= 0) {
    return ''
  }
  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  const dateObj = date instanceof Date ? date : new Date(timestamp)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}

function compareElapsedTimeAtLastControl(a, b) {
    if (!a.lastMarker && !b.lastMarker) {
        return a.number - b.number
    }
    if (!a.lastMarker) {
        return 1
    }
    if (!b.lastMarker) {
        return -1
    }
    if (a.lastMarker.id == b.lastMarker.id) {
        let aDate = new Date(a.lastSplit.time.replace(' ', 'T') + 'Z')
        let bDate = new Date(b.lastSplit.time.replace(' ', 'T') + 'Z')
        if (aDate.getTime() == bDate.getTime()) {
            return a.number - b.number
        } else {
            return timeFromStartToSplit(a, a.lastSplit) - timeFromStartToSplit(b, b.lastSplit)
        }
    } else {
        if (a.lastMarker.sort > b.lastMarker.sort) {
            if (a.lastMarker.sort - 1 == b.lastMarker.sort) {
                if (a.splits[b.lastMarker.id]) {
              if (a.splits[b.lastMarker.id].time < b.lastSplit.time && timeFromStart(a) < timeFromStartToSplit(b, b.lastSplit)) {
                return -1
                    }
                }
            }
            return -1
        } else {
            if (b.lastMarker.sort - 1 == a.lastMarker.sort) {
                if (b.splits[a.lastMarker.id]) {
              if (a.lastSplit.time < b.splits[a.lastMarker.id].time && timeFromStart(a) < timeFromStartToSplit(b, b.lastSplit)) {
                return -1
                    }
                }
            }
            return 1
        }
    }
}

// Memoized: only recomputed when the underlying results actually change,
// instead of on every render (critical once there are hundreds/thousands of rows).
const sortedResults = computed(() => {
    const list = resultsStore.results.filter(r => r.category_id !== 10)
    const hasPointScores = list.some(runner => runner.pointScore !== null && runner.pointScore > 0)
    return list.slice().sort((a, b) => {
        if (a.abs) return 1
        if (b.abs) return -1
        if (a.dnf) return 1
        if (b.dnf) return -1

        if (hasPointScores) {
            const aPoints = a.pointScore || 0
            const bPoints = b.pointScore || 0

            if ((aPoints > 0) === (bPoints > 0)) {
              if (aPoints > 0) {
                if (aPoints !== bPoints) {
                  return bPoints - aPoints
                }
                if (a.elapsedTime && b.elapsedTime) {
                  return a.elapsedTime - b.elapsedTime
                }
              } else {
                if (a.elapsedTime && b.elapsedTime) {
                  return a.elapsedTime - b.elapsedTime
                }
              }
            } else {
              return bPoints > 0 ? 1 : -1
            }
        } else {
            if (a.elapsedTime && b.elapsedTime) {
              return a.elapsedTime - b.elapsedTime
            }
        }

        if (a.finish_time || a.finish_time_gps || b.finish_time || b.finish_time_gps) {
            if (a.finish_time || a.finish_time_gps) return -1
            if (b.finish_time || b.finish_time_gps) return 1
        } else {
            return compareElapsedTimeAtLastControl(a, b)
        }
    })
})

const top3 = computed(() => sortedResults.value.slice(0, 3))

// Per-category podiums, skipping any category whose top 3 is the same set of
// runners as the overall podium (no point showing it twice).
const categoryPodiums = computed(() => {
  if (!resultsStore.shouldShowCategoryColumn()) return []

  const overallIds = new Set(top3.value.map(r => r.id))
  const groups = new Map()
  sortedResults.value.forEach(r => {
    if (r.category_id == null) return
    if (!groups.has(r.category_id)) groups.set(r.category_id, [])
    const arr = groups.get(r.category_id)
    if (arr.length < 3) arr.push(r)
  })

  const podiums = []
  groups.forEach((runners, categoryId) => {
    const isSameAsOverall = runners.length === overallIds.size && runners.every(r => overallIds.has(r.id))
    if (isSameAsOverall) return
    const category = resultsStore.categories.find(c => c.id == categoryId)
    podiums.push({
      categoryId,
      categoryName: category ? category.name : `Kategoria ${categoryId}`,
      runners
    })
  })
  return podiums
})

// Women's and men's podiums (only in events that have gender data)
const genderPodiums = computed(() => {
  if (!resultsStore.hasGender()) return []
  return [
    { gender: 'K', title: 'Podium kobiet' },
    { gender: 'M', title: 'Podium mężczyzn' }
  ].map(({ gender, title }) => ({
    gender,
    title,
    runners: sortedResults.value
      .filter(r => r.gender === gender && !r.abs && !r.dnf && (r.finish_time || r.finish_time_gps))
      .slice(0, 3)
  }))
})

const stats = computed(() => {
  const list = sortedResults.value
  let finished = 0
  let dnfAbs = 0
  list.forEach(r => {
    if (r.dnf || r.abs) dnfAbs++
    else if (r.finish_time || r.finish_time_gps) finished++
  })
  return {
    total: list.length,
    finished,
    onCourse: list.length - finished - dnfAbs,
    dnfAbs
  }
})

const markers = computed(() => {
  if (!resultsStore.markersMap) return []
  return Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
})

const lastMarkerIsFinish = computed(() => {
  const list = markers.value
  if (list.length === 0) return false
  const lastMarker = list[list.length - 1]
  return lastMarker.type == 8 || lastMarker.type == 93
})

const totalColumns = computed(() => {
  let count = 3 // #, zawodnik, start
  if (resultsStore.shouldShowCategoryColumn()) count++
  if (resultsStore.shouldShowPointsColumn()) count++
  count += markers.value.length
  if (!lastMarkerIsFinish.value) count++
  count += 2 // czas, strata
  return count
})

function formatPenaltyLabel(penaltySeconds) {
  const totalSeconds = Math.round(Number(penaltySeconds) || 0)
  if (totalSeconds <= 0) return ''

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    const minutesPart = minutes > 0 ? `${minutes}${seconds > 0 ? `:${String(seconds).padStart(2, '0')}` : ''}min` : ''
    const secondsPart = minutes === 0 && seconds > 0 ? `${seconds}s` : ''
    return `+${hours}h${minutesPart || secondsPart ? minutesPart || secondsPart : ''}`
  }

  if (minutes > 0) {
    if (seconds === 0) return `+${minutes}min`
    return `+${minutes}:${String(seconds).padStart(2, '0')}min`
  }

  return `+${seconds}s`
}

// --- Virtual scrolling -------------------------------------------------
// Only the rows currently in view (+ a small buffer) are rendered into the
// DOM. Two spacer rows reserve the space for everything above/below so the
// scrollbar and sticky header behave as if the full table were rendered.
// This keeps rendering cost constant regardless of participant count.
const ROW_HEIGHT = 44
const BUFFER_ROWS = 8

const tableScrollRef = ref(null)
const scrollTop = ref(0)
const viewportHeight = ref(600)
let resizeObserver = null

function onScroll(e) {
  scrollTop.value = e.target.scrollTop
}

onMounted(() => {
  if (tableScrollRef.value) {
    viewportHeight.value = tableScrollRef.value.clientHeight
    resizeObserver = new ResizeObserver(() => {
      viewportHeight.value = tableScrollRef.value.clientHeight
    })
    resizeObserver.observe(tableScrollRef.value)
  }
})

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect()
})

const genderFilter = ref('all')
const tableResults = computed(() => resultsStore.filterByGender(sortedResults.value, genderFilter.value))

const startIndex = computed(() => Math.max(0, Math.floor(scrollTop.value / ROW_HEIGHT) - BUFFER_ROWS))
const visibleRowCount = computed(() => Math.ceil(viewportHeight.value / ROW_HEIGHT) + BUFFER_ROWS * 2)
const endIndex = computed(() => Math.min(tableResults.value.length, startIndex.value + visibleRowCount.value))

const visibleRows = computed(() =>
  tableResults.value.slice(startIndex.value, endIndex.value).map((result, i) => ({
    result,
    rank: startIndex.value + i + 1
  }))
)

const topSpacerHeight = computed(() => startIndex.value * ROW_HEIGHT)
const bottomSpacerHeight = computed(() => Math.max(0, (tableResults.value.length - endIndex.value) * ROW_HEIGHT))

function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false

  recentlyChanged.value[result.id] = Date.now()

  const idx = sortedResults.value.findIndex(r => r.id === result.id)
  if (idx === -1 || !tableScrollRef.value) return

  const targetScrollTop = Math.max(0, idx * ROW_HEIGHT - viewportHeight.value / 2 + ROW_HEIGHT / 2)
  nextTick(() => {
    tableScrollRef.value.scrollTop = targetScrollTop
    scrollTop.value = targetScrollTop
  })
}
</script>

<template>
  <div class="live-board">
    <EventSwitcher />

    <div class="board-header">
      <div class="board-title">
        <span class="live-dot"></span>
        <h1>Tablica wyników na żywo</h1>
      </div>
      <div class="board-meta">
        <span class="badge bg-info">Aktualizacja co 30 sekund</span>
        <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
      </div>
    </div>

    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-value">{{ stats.total }}</div>
        <div class="stat-label">Uczestnicy</div>
      </div>
      <div class="stat-card stat-oncourse">
        <div class="stat-value">{{ stats.onCourse }}</div>
        <div class="stat-label">Na trasie</div>
      </div>
      <div class="stat-card stat-finished">
        <div class="stat-value">{{ stats.finished }}</div>
        <div class="stat-label">Zakończyło</div>
      </div>
      <div class="stat-card stat-dnf">
        <div class="stat-value">{{ stats.dnfAbs }}</div>
        <div class="stat-label">DNS/DNF</div>
      </div>
    </div>

    <GenderFilter v-model="genderFilter" />
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            v-model="searchQuery"
            class="form-control search-input"
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>

        <div v-if="showDropdown" class="search-dropdown">
          <div
            v-for="result in filteredResults"
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="board-layout">
      <div class="board-main">
        <div class="settings">
          <input type="checkbox" v-model="showElapsedTime" class="form-check-input" id="elapsedTimeToggle" />
          <label class="form-check-label" for="elapsedTimeToggle">Czas narastająco</label>
        </div>

        <div class="table-scroll" ref="tableScrollRef" @scroll="onScroll">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Zawodnik</th>
                <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
                <th v-if="resultsStore.shouldShowPointsColumn()">Punkty</th>
                <th>Start</th>
                <th v-for="marker in markers" :key="marker.id" class="marker-column">
                    <span v-html="formatMarkerText(marker.text)"></span>
                </th>
                <th v-if="!lastMarkerIsFinish">Meta</th>
                <th>Czas</th>
                <th>Strata</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="topSpacerHeight > 0" class="spacer-row" :style="{ height: topSpacerHeight + 'px' }">
                <td :colspan="totalColumns"></td>
              </tr>
              <tr
                v-for="{ result, rank } in visibleRows"
                :key="result.id"
                :data-result-id="result.id"
                :class="{ 'row-changed': recentlyChanged[result.id] }"
              >
                <td>{{ rank }}</td>
                <td class="name-cell"><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
                <td v-if="resultsStore.shouldShowCategoryColumn()">
                  {{ result.categoryPosition }}<span v-if="result.genderPosition" class="text-muted"> · {{ result.genderPosition }}</span>
                </td>
                <td v-if="resultsStore.shouldShowPointsColumn()">
                  {{ result.pointScore || 0 }}
                </td>
                <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
                <td v-for="(marker, index) in markers" :key="marker.id" class="marker-cell">
                  <span v-if="index == markers.length - 1 && lastMarkerIsFinish">
                      {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
                      <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
                  </span>
                    <span v-else-if="result.splits[marker.id]">
                        <span v-if="showElapsedTime">
                            {{ result.splits[marker.id].time ? getHHMMSSString(timeFromStartToSplit(result, result.splits[marker.id])) : '--:--:--' }}
                        </span>
                        <span v-else>
                            {{ result.splits[marker.id].time ? new Date(result.splits[marker.id].time).toLocaleTimeString() : '--:--:--' }}
                        </span>
                    </span>
                </td>
                <td v-if="!lastMarkerIsFinish" class="marker-cell">
                  {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
                  <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
                </td>
                <td>
                  <span v-if="result.dnf || result.abs" :class="getResultClass(result)">{{ result.abs? "DNS" : "DNF" }}</span>
                  <span v-else :class="getResultClass(result)">
                    {{ result.finish_time ? result.timeScore : (result.finish_time_gps ? result.timeScore : (result.start_time ? calculateTimeFromStart(result) : '--:--:--')) }}
                  </span>
                  <span
                    v-if="result.penaltySeconds"
                    class="badge bg-warning text-dark penalty-badge"
                    :title="result.penaltyComment || 'Kara czasowa'"
                  >
                    {{ formatPenaltyLabel(result.penaltySeconds) }}
                  </span>
                </td>
                <td>
                  {{ formatGapTime(resultsStore.getGapToLeader(result)) }}
                </td>
              </tr>
              <tr v-if="bottomSpacerHeight > 0" class="spacer-row" :style="{ height: bottomSpacerHeight + 'px' }">
                <td :colspan="totalColumns"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <aside class="board-sidebar">
        <div class="podium-card">
          <h2>Podium</h2>
          <ul class="podium-list">
            <li v-for="(result, index) in top3" :key="result.id" :class="`podium-item podium-${index + 1}`">
              <span class="podium-rank">{{ index + 1 }}</span>
              <div class="podium-info">
                <div class="podium-name">{{ result.name }}</div>
                <div class="podium-time">{{ result.timeScore || result.pointScore || '--:--:--' }}</div>
              </div>
            </li>
            <li v-if="top3.length === 0" class="podium-empty">Brak danych</li>
          </ul>
        </div>

        <div v-for="genderPodium in genderPodiums" :key="genderPodium.gender" class="podium-card">
          <h2>{{ genderPodium.title }}</h2>
          <ul class="podium-list">
            <li v-for="(result, index) in genderPodium.runners" :key="result.id" :class="`podium-item podium-${index + 1}`">
              <span class="podium-rank">{{ index + 1 }}</span>
              <div class="podium-info">
                <div class="podium-name">{{ result.name }}</div>
                <div class="podium-time">{{ result.timeScore || result.pointScore || '--:--:--' }}</div>
              </div>
            </li>
            <li v-if="genderPodium.runners.length === 0" class="podium-empty">Brak danych</li>
          </ul>
        </div>

        <div v-for="categoryPodium in categoryPodiums" :key="categoryPodium.categoryId" class="podium-card">
          <h2>{{ categoryPodium.categoryName }}</h2>
          <ul class="podium-list">
            <li v-for="(result, index) in categoryPodium.runners" :key="result.id" :class="`podium-item podium-${index + 1}`">
              <span class="podium-rank">{{ index + 1 }}</span>
              <div class="podium-info">
                <div class="podium-name">{{ result.name }}</div>
                <div class="podium-time">{{ result.timeScore || result.pointScore || '--:--:--' }}</div>
              </div>
            </li>
          </ul>
        </div>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.live-board {
  --board-accent: #0d6efd;
}

.board-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.board-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.board-title h1 {
  margin: 0;
  font-size: 1.5rem;
}

.live-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #dc3545;
  box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.6);
  animation: pulse 1.6s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.6); }
  70% { box-shadow: 0 0 0 8px rgba(220, 53, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
}

.board-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 1rem;
}

.stat-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.6rem;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.1;
}

.stat-label {
  font-size: 0.78rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.stat-oncourse .stat-value { color: #fd7e14; }
.stat-finished .stat-value { color: #198754; }
.stat-dnf .stat-value { color: #6c757d; }

.board-layout {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.board-main {
  flex: 1 1 auto;
  min-width: 0;
}

.board-sidebar {
  flex: 0 0 260px;
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}

.podium-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.podium-card h2 {
  font-size: 1.05rem;
  margin-bottom: 0.75rem;
}

.podium-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.podium-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
  border-radius: 0.4rem;
  background: #f8f9fa;
}

.podium-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  font-weight: 700;
  background: #dee2e6;
  flex-shrink: 0;
}

.podium-1 .podium-rank { background: #ffd43b; }
.podium-2 .podium-rank { background: #ced4da; }
.podium-3 .podium-rank { background: #e0b88a; }

.podium-info {
  min-width: 0;
}

.podium-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.podium-time {
  font-size: 0.85rem;
  color: #6c757d;
}

.podium-empty {
  color: #6c757d;
  font-style: italic;
}

.table {
    text-align: left;
}

.marker-column {
    text-align: center;
}

.table td.marker-cell {
    text-align: center;
}

.name-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.result-upcoming {
  font-style: italic;
}

.table-scroll {
  width: 100%;
  height: 70vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

/* Fixed row height keeps the virtual-scroll math accurate at any participant count */
.table tbody tr:not(.spacer-row) {
  height: 44px;
}

.spacer-row td {
  padding: 0;
  border: none;
}

.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: var(--sledz-highlight) !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}
@keyframes highlight-row {
  0% { background: var(--sledz-highlight-flash); }
  100% { background: var(--sledz-highlight); }
}

.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

.penalty-badge {
  margin-left: 6px;
  vertical-align: middle;
}

@media (max-width: 900px) {
  .board-layout {
    flex-direction: column;
  }
  .board-sidebar {
    flex: 1 1 auto;
    position: static;
  }
  .stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
